import { useState, useRef, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  POPOVER,
  TRIGGER_CONTENT,
  triggerClass,
  useDropUp,
  type NotionVariant,
} from './notionControl';

interface NotionTimePickerProps {
  value: string; // HH:MM format (24-hour)
  onChange: (value: string) => void;
  className?: string;
  variant?: NotionVariant;
}

const LIST_HEIGHT = 248;

export function NotionTimePicker({
  value,
  onChange,
  className = '',
  variant = 'field',
}: NotionTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  const dropUp = useDropUp(isOpen, triggerRef, LIST_HEIGHT);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    // Kept off the drawer's Escape handler — see the note in NotionSelect.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, true);
    // Scroll to selected time instantly
    const raf = requestAnimationFrame(() => {
      selectedRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
    });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
      cancelAnimationFrame(raf);
    };
  }, [isOpen]);

  const formatDisplayTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTime24to12 = (hour: number, minute: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${String(minute).padStart(2, '0')} ${ampm}`;
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const time12 = formatTime24to12(hour, minute);
        slots.push({ value: time24, label: time12 });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleSelect = (timeValue: string) => {
    onChange(timeValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={variant === 'field' ? 'relative block w-full' : 'relative inline-block'}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={triggerClass(variant, className)}
      >
        <span className={TRIGGER_CONTENT}>
          {value ? formatDisplayTime(value) : <span className="text-zinc-400">Select time</span>}
        </span>
        <Clock size={15} className="shrink-0 text-zinc-400" aria-hidden />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: dropUp ? 6 : -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dropUp ? 6 : -6 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 ${POPOVER} ${
              variant === 'field' ? 'w-full min-w-[9rem]' : 'w-[9rem]'
            } ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          >
            <div className="max-h-[240px] overflow-y-auto py-1">
              {timeSlots.map(slot => {
                const isSelected = slot.value === value;
                return (
                  <button
                    key={slot.value}
                    ref={isSelected ? selectedRef : null}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(slot.value)}
                    className={`w-full px-3 py-2 text-left font-mono text-[13px] transition-colors hover:bg-paper-tint ${
                      isSelected ? 'bg-paper-tint font-medium text-primary' : 'text-zinc-600'
                    }`}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
